import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: LucideIcon;
  trend?: {
    value: string;
    isPositive: boolean;
  };
  colorScheme?: "blue" | "green" | "amber" | "purple" | "red";
  isLoading?: boolean;
}

const colorClasses = {
  blue: {
    border: "border-l-blue-500",
    gradient: "bg-gradient-to-br from-blue-50/30 to-transparent dark:from-blue-950/20",
    icon: "text-blue-600 dark:text-blue-400",
  },
  green: {
    border: "border-l-green-500",
    gradient: "bg-gradient-to-br from-green-50/30 to-transparent dark:from-green-950/20",
    icon: "text-green-600 dark:text-green-400",
  },
  amber: {
    border: "border-l-amber-500",
    gradient: "bg-gradient-to-br from-amber-50/30 to-transparent dark:from-amber-950/20",
    icon: "text-amber-600 dark:text-amber-400",
  },
  purple: {
    border: "border-l-purple-500",
    gradient: "bg-gradient-to-br from-purple-50/30 to-transparent dark:from-purple-950/20",
    icon: "text-purple-600 dark:text-purple-400",
  },
  red: {
    border: "border-l-red-500",
    gradient: "bg-gradient-to-br from-red-50/30 to-transparent dark:from-red-950/20",
    icon: "text-red-600 dark:text-red-400",
  },
};

export function KPICard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  colorScheme = "blue",
  isLoading = false,
}: KPICardProps) {
  const colors = colorClasses[colorScheme];

  if (isLoading) {
    return (
      <Card className={cn("border-l-4", colors.border, colors.gradient)}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">{title}</CardTitle>
          <Icon className={cn("h-4 w-4", colors.icon)} />
        </CardHeader>
        <CardContent>
          <div className="h-8 w-24 animate-pulse rounded bg-muted" />
          {subtitle && <div className="mt-1 h-4 w-32 animate-pulse rounded bg-muted" />}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("border-l-4", colors.border, colors.gradient)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={cn("h-4 w-4", colors.icon)} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        {trend && (
          <p
            className={cn(
              "mt-1 text-xs font-medium",
              trend.isPositive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
            )}
          >
            {trend.value}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
